import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguageChange } from '@/hooks/use-language-change';
import { languageNames, orderedLanguages, type SupportedLanguage } from '@/i18n';

interface LanguageSwitcherProps {
  /** When true, shows a toast after first language selection */
  isFirstLogin?: boolean;
}

export function LanguageSwitcher({ isFirstLogin = false }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const { changeLanguage } = useLanguageChange({
    showFirstLoginToast: isFirstLogin,
  });

  const currentLang = (i18n.language || 'en') as SupportedLanguage;
  // Handle language codes like 'en-US' by taking the base language
  const normalizedLang = currentLang.split('-')[0] as SupportedLanguage;
  const displayLang = languageNames[currentLang] ? currentLang : normalizedLang;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 text-inherit hover:text-inherit hover:bg-white/20"
        >
          <Globe className="h-4 w-4" />
          <span className="sr-only">Change language</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {orderedLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang}
            onClick={() => changeLanguage(lang)}
            className={displayLang === lang ? 'bg-accent' : ''}
          >
            {languageNames[lang]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
